import { useNavigate } from 'react-router-dom';
import { Check } from 'lucide-react';
import { Badge } from '@/components/atoms/badge';
import { cn } from '@/lib/utils';
import type { ProductGroupItem, VariationType } from '@/lib/types';

interface AttributeChipsProps {
  items: ProductGroupItem[];
  currentProductId: string;
  variationType: VariationType;
  onProductClick?: (refId: string) => void;
}

// Get the label for a product group item based on variation type
function getItemLabel(item: ProductGroupItem, variationType: VariationType): string {
  switch (variationType) {
    case 'style':
      return item.styleName || '-';
    case 'pattern':
      return item.patternName || '-';
    case 'material':
      return item.materialName || '-';
    case 'flavor':
      return item.flavorName || '-';
    case 'quantity':
      return item.quantityLabel || '-';
    default:
      return item.product.name || '-';
  }
}

export function AttributeChips({
  items,
  currentProductId,
  variationType,
  onProductClick,
}: AttributeChipsProps) {
  const navigate = useNavigate();

  const handleClick = (refId: string) => {
    if (refId !== currentProductId) {
      if (onProductClick) {
        onProductClick(refId);
      } else {
        navigate(`/products/${refId}`);
      }
    }
  };

  return (
    <div className="flex gap-2 flex-wrap">
      {items.map((item) => {
        const isCurrent = item.product.refId === currentProductId;
        const label = getItemLabel(item, variationType);

        return (
          <Badge
            key={item.id}
            variant={isCurrent ? 'default' : 'outline'}
            className={cn(
              'cursor-pointer transition-all px-3 py-1 gap-1',
              isCurrent
                ? 'bg-primary text-primary-foreground'
                : 'hover:border-primary hover:bg-primary/5',
              !isCurrent && 'cursor-pointer',
              isCurrent && 'cursor-default'
            )}
            onClick={() => handleClick(item.product.refId)}
          >
            {isCurrent && <Check className="size-3" />}
            {label}
          </Badge>
        );
      })}
    </div>
  );
}
